import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CancelSvg, EyeOffSvg, EyeSmallSvg } from "../../svg/Index";
import {
  login,
  socialLogin,
  setLoginFormField,
  resetLoginForm,
  closeLoginModal,
  togglePassword,
  clearLoginSuccess,
  clearErrors,
  clearMessage,
  forgotPasswordSendMail,
  forgotPasswordVerifyOTP,
  resetPassword,
  googleLoginS
} from "../slices/authSlice";
import { ErrorMessage, SuccessMessage } from "../common/Helper";
import { fetchCart } from "../slices/cartSlice";
import { GoogleLogin } from '@react-oauth/google';

const Login = ({
  isOpen = false,
  onClose = () => { },
  onLogin = (data) => { },
  onSocialLogin = (provider) => { },
  onSignupClick = () => { },
  title = "Hi, Welcome Back!",
  subtitle = "Sign in to access your account and opportunities."
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation()
  const {
    loginForm = {
      email: '',
      password: '',
      rememberMe: false
    },
    showPassword = false,
    errors = {},
    loading = false,
    socialLoading = false,
    error = null,
    message = { type: '', text: '' },
    isAuthenticated = false
  } = useSelector((state) => state?.auth || {});

  const [forgotStep, setForgotStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOTP, setForgotOTP] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showForgotFlow, setShowForgotFlow] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState({
    send: false,
    verify: false,
    reset: false,
    resend: false
  });

  const [localErrors, setLocalErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const authHandledRef = useRef(false);

  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setResendEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  useEffect(() => {
    if (isAuthenticated && !authHandledRef.current) {
      authHandledRef.current = true;

      onLogin(loginForm);
      dispatch(closeLoginModal());
      onClose();
      dispatch(resetLoginForm());

      setTimeout(() => {
        dispatch(clearLoginSuccess?.());
        dispatch(clearMessage());
        authHandledRef.current = false;
      }, 3000);
    }

    if (!isAuthenticated) {
      authHandledRef.current = false;
    }
  }, [isAuthenticated, loginForm, onLogin, dispatch, onClose]);

  useEffect(() => {
    if (error || message.text) {
      const timer = setTimeout(() => {
        dispatch(clearMessage());
        dispatch(clearErrors());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, message.text, dispatch]);

  useEffect(() => {
    if (isOpen) {
      const modalElement = document.getElementById('loginModal');
      if (modalElement && !modalElement.classList.contains('show')) {
        try {
          const modal = new window.bootstrap.Modal(modalElement);
          modal.show();
        } catch (e) {
          console.warn('Bootstrap modal error:', e);
        }
      }
      setShowForgotFlow(false);
      setForgotStep(1);
      setForgotEmail('');
      setForgotOTP('');
      setNewPassword('');
      setConfirmPassword('');
      setOtpTimer(0);
      setResendEnabled(false);
    }
  }, [isOpen]);

   const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const guestCart = JSON.parse(localStorage.getItem('guest_cart')) || { items: [] };
      const result = await dispatch(googleLoginS({ 
        token: credentialResponse,
        guest_cart: guestCart.items || []
      }));

      if (googleLoginS.fulfilled.match(result)) {
        dispatch(fetchCart());
        SuccessMessage(result.payload?.message || 'Login successful!');
        const modalElement = document.getElementById('loginModal');
        const closeButton = modalElement?.querySelector('[data-bs-dismiss="modal"]');
        if (closeButton) {
          closeButton.click();
        }
        
        if (location.pathname === '/signup') {
          navigate("/dashboard");
        }
        onLogin(result.payload.user);
        onClose();
      } else if (googleLoginS.rejected.match(result)) {
        ErrorMessage(result.payload || 'Google login failed');
      }
    } catch (error) {
      console.error("Google login failed:", error);
      ErrorMessage('Google login failed. Please try again.');
    }
  };


  const togglePasswordVisibility = () => {
    dispatch(togglePassword());
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields({
      ...touchedFields,
      [name]: true
    });

    validateField(name, e.target.value);
  };

  const validateField = (fieldName, value) => {
    let error = '';

    switch (fieldName) {
      case 'email':
        if (!value) {
          error = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 6) {
          error = 'Password must be at least 6 characters';
        }
        break;

      case 'forgotEmail':
        if (!value) {
          error = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'forgotOTP':
        if (!value) {
          error = 'OTP is required';
        } else if (!/^\d{6}$/.test(value)) {
          error = 'OTP must be 6 digits';
        }
        break;

      case 'newPassword':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 8) {
          error = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          error = 'Password must include uppercase, lowercase, and number';
        }
        break;

      case 'confirmPassword':
        if (!value) {
          error = 'Please confirm your password';
        } else if (value !== newPassword) {
          error = 'Passwords do not match';
        }
        break;

      default:
        break;
    }

    setLocalErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return error;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (showForgotFlow) {
      switch (name) {
        case 'forgotEmail':
          setForgotEmail(value);
          break;
        case 'forgotOTP':
          const otpValue = value.replace(/\D/g, '').slice(0, 6);
          setForgotOTP(otpValue);
          break;
        case 'newPassword':
          setNewPassword(value);
          break;
        case 'confirmPassword':
          setConfirmPassword(value);
          break;
        default:
          break;
      }
    } else {
      dispatch(setLoginFormField({
        name,
        value: type === "checkbox" ? checked : value
      }));
    }

    if (localErrors[name]) {
      setLocalErrors({
        ...localErrors,
        [name]: ""
      });
    }

    if (errors[name]) {
      dispatch(clearErrors());
    }
  };

  const validateForm = () => {
    const newErrors = {};

    Object.keys(loginForm).forEach(field => {
      if (field === 'rememberMe') return;
      const error = validateField(field, loginForm[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    const allTouched = {};
    Object.keys(loginForm).forEach(field => {
      if (field !== 'rememberMe') {
        allTouched[field] = true;
      }
    });
    setTouchedFields(allTouched);

    return newErrors;
  };

  const validateForgotEmail = () => {
    const newErrors = {};
    if (!forgotEmail.trim()) {
      newErrors.forgotEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(forgotEmail)) {
      newErrors.forgotEmail = 'Please enter a valid email address';
    }
    return newErrors;
  };

  const validateOTP = () => {
    const newErrors = {};
    if (!forgotOTP.trim()) {
      newErrors.forgotOTP = 'OTP is required';
    } else if (!/^\d{6}$/.test(forgotOTP)) {
      newErrors.forgotOTP = 'OTP must be 6 digits';
    }
    return newErrors;
  };

  const validateNewPassword = () => {
    const newErrors = {};
    if (!newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      newErrors.newPassword = 'Password must include uppercase, lowercase, and number';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const validationErrors = validateForm();

    const hasErrors = Object.values(validationErrors).some(error => error !== '');

    if (hasErrors) {
      return;
    }

    try {
      const result = await dispatch(login({
        email: loginForm.email,
        password: loginForm.password
      }));

      if (login.fulfilled.match(result)) {
          dispatch(fetchCart())
        if (result.payload?.message) {
          SuccessMessage(result.payload.message);
          if (location.pathname === '/signup') {
            navigate("/dashboard");
          }
        }
        const modalElement = document.getElementById('loginModal');
        const closeButton = modalElement.querySelector('[data-bs-dismiss="modal"]');
        if (closeButton) {
          closeButton.click();
        }
        dispatch(resetLoginForm());
        setLocalErrors({});
        setTouchedFields({});

      } else if (login.rejected.match(result)) {
        ErrorMessage(result.payload);
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleSendOTP = async () => {
    const emailErrors = validateForgotEmail();
    if (Object.keys(emailErrors).length > 0) {
      setLocalErrors(emailErrors);
      return;
    }

    setForgotLoading(prev => ({ ...prev, send: true }));
    try {
      const result = await dispatch(forgotPasswordSendMail(forgotEmail));

      if (forgotPasswordSendMail.fulfilled.match(result)) {
        setOtpTimer(60);
        setResendEnabled(false);
        setForgotStep(2);
        SuccessMessage(result.payload?.message || 'OTP sent successfully!');
      } else if (forgotPasswordSendMail.rejected.match(result)) {
        ErrorMessage(result.payload || 'Failed to send OTP');
      }
    } catch (error) {
      ErrorMessage('Failed to send OTP');
    } finally {
      setForgotLoading(prev => ({ ...prev, send: false }));
    }
  };

  const handleResendOTP = async () => {
    setForgotLoading(prev => ({ ...prev, resend: true }));
    try {
      const result = await dispatch(forgotPasswordSendMail(forgotEmail));

      if (forgotPasswordSendMail.fulfilled.match(result)) {
        setOtpTimer(60);
        setResendEnabled(false);
        SuccessMessage(result.payload?.message || 'OTP resent successfully!');
      } else if (forgotPasswordSendMail.rejected.match(result)) {
        ErrorMessage(result.payload || 'Failed to resend OTP');
      }
    } catch (error) {
      ErrorMessage('Failed to resend OTP');
    } finally {
      setForgotLoading(prev => ({ ...prev, resend: false }));
    }
  };

  const handleVerifyOTP = async () => {
    const otpErrors = validateOTP();
    if (Object.keys(otpErrors).length > 0) {
      setLocalErrors(otpErrors);
      return;
    }

    setForgotLoading(prev => ({ ...prev, verify: true }));
    try {
      const result = await dispatch(forgotPasswordVerifyOTP({
        email: forgotEmail,
        otp: forgotOTP
      }));

      if (forgotPasswordVerifyOTP.fulfilled.match(result)) {
        setForgotStep(3);
        SuccessMessage(result.payload?.message || 'OTP verified successfully!');
      } else if (forgotPasswordVerifyOTP.rejected.match(result)) {
        ErrorMessage(result.payload || 'Failed to verify OTP');
      }
    } catch (error) {
      ErrorMessage('Failed to verify OTP');
    } finally {
      setForgotLoading(prev => ({ ...prev, verify: false }));
    }
  };

  const handleResetPassword = async () => {
    const passwordErrors = validateNewPassword();
    if (Object.keys(passwordErrors).length > 0) {
      setLocalErrors(passwordErrors);
      return;
    }

    setForgotLoading(prev => ({ ...prev, reset: true }));
    try {
      const result = await dispatch(resetPassword({
        email: forgotEmail,
        password: newPassword,
        password_confirmation: confirmPassword
      }));

      if (resetPassword.fulfilled.match(result)) {
        SuccessMessage(result.payload?.message || 'Password reset successfully!');
        setShowForgotFlow(false);
        setForgotStep(1);
        setForgotEmail('');
        setForgotOTP('');
        setNewPassword('');
        setConfirmPassword('');
      } else if (resetPassword.rejected.match(result)) {
        ErrorMessage(result.payload || 'Failed to reset password');
      }
    } catch (error) {
      ErrorMessage('Failed to reset password');
    } finally {
      setForgotLoading(prev => ({ ...prev, reset: false }));
    }
  };

  const handleSocialLogin = (providerId) => {
    dispatch(socialLogin(providerId));
    onSocialLogin(providerId);
  };

  const handleClose = () => {
    dispatch(closeLoginModal());
    dispatch(resetLoginForm());
    setLocalErrors({});
    setTouchedFields({});
    setShowForgotFlow(false);
    setForgotStep(1);
    setForgotEmail('');
    setForgotOTP('');
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  const getFieldError = (fieldName) => {
    if (touchedFields[fieldName] && localErrors[fieldName]) {
      return localErrors[fieldName];
    }
    if (errors[fieldName] && !localErrors[fieldName]) {
      return errors[fieldName];
    }
    return '';
  };

  const handleForgotPassword = () => {
    setShowForgotFlow(true);
    setForgotStep(1);
    setForgotEmail('');
    setForgotOTP('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleBackToLogin = () => {
    setShowForgotFlow(false);
    setForgotStep(1);
    setForgotEmail('');
    setForgotOTP('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    dispatch(closeLoginModal());
    const modalElement = document.getElementById('loginModal');
    const closeButton = modalElement.querySelector('[data-bs-dismiss="modal"]');
    if (closeButton) {
      closeButton.click();
    }
    onClose();
    onSignupClick();
    navigate('/signup');
  };

  const socialProviders = [
    { id: "google", name: "Google", icon: "/image/google.svg", className: "white" },
  ];

  const displayErrors = { ...localErrors, ...errors };

  const renderForgotStep1 = () => (
    <div className="login-fileds">
      <div className="login-header mb-4 text-center">
        <h5 className="mb-2">Reset Password</h5>
        <p>Enter your email to receive verification code</p>
      </div>

      <div className="form-group mb-3 position-relative">
        <input
          type="email"
          className={`form-control ${localErrors.forgotEmail ? 'is-invalid' : ''}`}
          placeholder="Enter your email address"
          name="forgotEmail"
          value={forgotEmail}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={forgotLoading.send}
        />
        <img src="/image/Profile.svg" alt="Email icon" />
        {localErrors.forgotEmail && (
          <div className="invalid-feedback">{localErrors.forgotEmail}</div>
        )}
      </div>

      <div className="form-group mb-4">
        <button
          type="button"
          className="btn primary-btn w-100"
          onClick={handleSendOTP}
          disabled={forgotLoading.send}
        >
          {forgotLoading.send ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Sending...
            </>
          ) : (
            'Send Verification Code'
          )}
        </button>
      </div>

      <p className="text-center mb-0 auth-bottom">
        <Link
          to="#"
          className="link-text text-decoration-underline"
          onClick={handleBackToLogin}
        >
          Back to Login
        </Link>
      </p>
    </div>
  );

  const renderForgotStep2 = () => (
    <div className="login-fileds">
      <div className="login-header mb-4 text-center">
        <h5 className="mb-2">Verify Email</h5>
        <p>We've sent a 6-digit code to {forgotEmail}</p>
      </div>

      <div className="form-group mb-3 position-relative">
        <input
          type="text"
          className={`form-control text-center ${localErrors.forgotOTP ? 'is-invalid' : ''}`}
          placeholder="Enter 6-digit code"
          name="forgotOTP"
          value={forgotOTP}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength="6"
          disabled={forgotLoading.verify}
        />
        {localErrors.forgotOTP && (
          <div className="invalid-feedback text-center">{localErrors.forgotOTP}</div>
        )}
      </div>

      <div className="otp-timer text-center mb-3">
        {otpTimer > 0 ? (
          <p className="small">
            Resend OTP in <span className="text-primary">{otpTimer}</span> seconds
          </p>
        ) : (
          <button
            type="button"
            className="btn btn-link p-0 small text-decoration-underline"
            onClick={handleResendOTP}
            disabled={!resendEnabled || forgotLoading.resend}
          >
            {forgotLoading.resend ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Resending...
              </>
            ) : (
              'Resend OTP'
            )}
          </button>
        )}
      </div>

      <div className="form-group mb-4">
        <button
          type="button"
          className="btn primary-btn w-100"
          onClick={handleVerifyOTP}
          disabled={forgotLoading.verify}
        >
          {forgotLoading.verify ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Verifying...
            </>
          ) : (
            'Verify OTP'
          )}
        </button>
      </div>

      <p className="text-center mb-0 auth-bottom">
        <Link
          to="#"
          className="link-text text-decoration-underline"
          onClick={() => setForgotStep(1)}
        >
          Back
        </Link>
      </p>
    </div>
  );

  const renderForgotStep3 = () => (
    <div className="login-fileds">
      <div className="login-header mb-4 text-center">
        <h5 className="mb-2">Set New Password</h5>
        <p>Create a new password for your account</p>
      </div>

      <div className="form-group mb-3 position-relative">
        <input
          type={showForgotPassword ? "text" : "password"}
          className={`form-control ${localErrors.newPassword ? 'is-invalid' : ''}`}
          placeholder="New Password"
          name="newPassword"
          value={newPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={forgotLoading.reset}
        />
        <img src="/image/Lock.svg" alt="Password icon" />
        <span
          className="eye-icon"
          onClick={() => setShowForgotPassword(!showForgotPassword)}
          style={{ cursor: "pointer" }}
        >
          {showForgotPassword ? <EyeOffSvg /> : <EyeSmallSvg />}
        </span>
        {localErrors.newPassword && (
          <div className="invalid-feedback">{localErrors.newPassword}</div>
        )}
      </div>

      <div className="form-group mb-3 position-relative">
        <input
          type={showForgotConfirmPassword ? "text" : "password"}
          className={`form-control ${localErrors.confirmPassword ? 'is-invalid' : ''}`}
          placeholder="Confirm Password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={forgotLoading.reset}
        />
        <img src="/image/Lock.svg" alt="Password icon" />
        <span
          className="eye-icon"
          onClick={() => setShowForgotConfirmPassword(!showForgotConfirmPassword)}
          style={{ cursor: "pointer" }}
        >
          {showForgotConfirmPassword ? <EyeOffSvg /> : <EyeSmallSvg />}
        </span>
        {localErrors.confirmPassword && (
          <div className="invalid-feedback">{localErrors.confirmPassword}</div>
        )}
      </div>

      <div className="form-group mb-4">
        <button
          type="button"
          className="btn primary-btn w-100"
          onClick={handleResetPassword}
          disabled={forgotLoading.reset}
        >
          {forgotLoading.reset ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Resetting...
            </>
          ) : (
            'Reset Password'
          )}
        </button>
      </div>

      <p className="text-center mb-0 auth-bottom">
        <Link
          to="#"
          className="link-text text-decoration-underline"
          onClick={handleBackToLogin}
        >
          Back to Login
        </Link>
      </p>
    </div>
  );

  const renderForgotFlow = () => {
    switch (forgotStep) {
      case 1:
        return renderForgotStep1();
      case 2:
        return renderForgotStep2();
      case 3:
        return renderForgotStep3();
      default:
        return renderForgotStep1();
    }
  };

  const renderLoginForm = () => (
    <div className="login-fileds">
      <div className="login-header mb-4 text-center">
        <h5 className="mb-2">{title}</h5>
        <p>{subtitle}</p>
      </div>

      <div className="form-group mb-3 position-relative">
        <input
          type="email"
          className={`form-control ${getFieldError('email') || displayErrors.email ? 'is-invalid' : ''}`}
          placeholder="Enter your email address"
          name="email"
          value={loginForm.email || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loading || socialLoading}
        />
        <img src="/image/Profile.svg" alt="Email icon" />
        {(getFieldError('email') || displayErrors.email) && (
          <div className="invalid-feedback">
            {getFieldError('email') || displayErrors.email}
          </div>
        )}
      </div>

      <div className="form-group mb-3 position-relative">
        <input
          type={showPassword ? "text" : "password"}
          className={`form-control ${getFieldError('password') || displayErrors.password ? 'is-invalid' : ''}`}
          placeholder="Password"
          name="password"
          value={loginForm.password || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loading || socialLoading}
        />
        <img src="/image/Lock.svg" alt="Password icon" />
        <span
          className="eye-icon"
          onClick={togglePasswordVisibility}
          style={{ cursor: "pointer" }}
        >
          {showPassword ? <EyeOffSvg /> : <EyeSmallSvg />}
        </span>
        {(getFieldError('password') || displayErrors.password) && (
          <div className="invalid-feedback">
            {getFieldError('password') || displayErrors.password}
          </div>
        )}
      </div>

      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="form-check"></div>
        <Link
          to="#"
          className="text-decoration-underline font--14"
          onClick={(e) => {
            e.preventDefault();
            handleForgotPassword();
          }}
        >
          Forgot Password?
        </Link>
      </div>

      <div className="form-group mb-4">
        <button
          type="submit"
          className="btn primary-btn w-100"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </button>
      </div>

      <div className="or-option text-center mb-4">
        <span className="d-block position-relative mb-3">
          or Sign In with
        </span>
        <div className="d-flex align-items-center justify-content-center gap-3">
          {/* {socialProviders.map((provider) => (
            <button
              key={provider.id}
              className={`option-signin ${provider.className} d-flex align-items-center justify-content-center gap-2`}
              onClick={() => handleSocialLogin(provider.id)}
              disabled={socialLoading || loading}
              style={{ border: 'none', cursor: 'pointer' }}
            >
              {socialLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <>
                  <img src={provider.icon} alt={provider.name} />
                  {provider.name}
                </>
              )}
            </button>
            
          ))} */}
           <GoogleLogin
                        onSuccess={(res) =>
                          handleGoogleSuccess(res.credential)}
                        onError={(err) =>
                          console.error('Login Failed ', err)} auto_select />
        </div>
      </div>

      <p className="text-center mb-0 auth-bottom">
        Don't have an account?{" "}
        <Link
          to="#"
          className="link-text text-decoration-underline"
          onClick={handleSignUp}
        >
          Sign Up
        </Link>
      </p>
    </div>
  );

  return (
    <>
      <div
        className="modal fade login-modal"
        id="loginModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="row align-items-center">
                <div className="col-md-6 order-md-0 order-1">
                  <div className="login-left mt-3 mt-md-0">
                    <img
                      src="/image/login-left.png"
                      className="img-fluid w-100"
                      alt=""
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="login-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                      onClick={handleClose}
                    >
                      <CancelSvg />
                    </button>
                  </div>

                  {showForgotFlow ? renderForgotFlow() : renderLoginForm()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;